import React, { useState, useEffect } from "react";
import {
  Users,
  Building,
  Crown,
  UserCog,
  MapPin,
  Package,
  Headphones,
  Phone,
  RefreshCw,
  TrendingUp,
  Activity,
  AlertCircle,
} from "lucide-react";
import { api } from "../../services/api";
import { User, Team, UserStats, FilterState, TabType } from "./types";
import { UsersTab } from "./UsersTab";
import { TeamsTab } from "./TeamsTab";
import { HierarchyTab } from "./HierarchyTab";
import { StatsTab } from "./StatsTab";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useLanguage } from "../../contexts/LanguageContext";

export const UsersMain: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>("users");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);

  // Filter states
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    selectedRole: "all",
    selectedCountry: "all",
    selectedTeam: "all",
    showActiveOnly: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users
      const token = localStorage.getItem("authToken");

      const usersResponse = await fetch(`${API_BASE_URL}/api/auth/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const usersData = await usersResponse.json();
      if (usersData.success) {
        setUsers(usersData.data.users);
      } else {
        setError(usersData.error);
      }

      // AlterCPA accounts removed for Serbia-CC
      // No longer needed - set empty array
      setAccounts([]);

      // Calculate stats
      const calculatedStats: UserStats = {
        totalUsers: usersData.success ? usersData.data.users.length : 0,
        activeUsers: usersData.success
          ? usersData.data.users.filter((u: User) => u.isActive).length
          : 0,
        inactiveUsers: usersData.success
          ? usersData.data.users.filter((u: User) => !u.isActive).length
          : 0,
        totalTeams: teams.length,
        usersByRole: {},
        usersByCountry: {},
      };

      // Calculate role distribution
      if (usersData.success) {
        usersData.data.users.forEach((user: User) => {
          calculatedStats.usersByRole[user.role] =
            (calculatedStats.usersByRole[user.role] || 0) + 1;
          user.countries.forEach((country) => {
            calculatedStats.usersByCountry[country] =
              (calculatedStats.usersByCountry[country] || 0) + 1;
          });
        });
      }

      setStats(calculatedStats);

      setLoading(false);
    } catch (error) {
      console.error("Error loading users data:", error);
      setError("Failed to load users data: " + error);
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters((prev: FilterState) => ({ ...prev, ...newFilters }));
  };

  const handleEditUser = (user: User) => {
    console.log("Edit user:", user);
    // Implementation for editing user
  };

  const handleDeleteUser = (userId: string) => {
    console.log("Delete user:", userId);
    // Implementation for deleting user
  };

  const handleToggleUserStatus = (userId: string) => {
    console.log("Toggle user status:", userId);
    // Implementation for toggling user status
  };

  const handleEditTeam = (team: Team) => {
    console.log("Edit team:", team);
    // Implementation for editing team
  };

  const handleDeleteTeam = (teamId: string) => {
    console.log("Delete team:", teamId);
    // Implementation for deleting team
  };

  const tabs = [
    { id: "users", label: t("users.tabUsers"), icon: Users },
    { id: "teams", label: t("users.tabTeams"), icon: Building },
    { id: "hierarchy", label: t("users.tabHierarchy"), icon: Crown },
    { id: "stats", label: t("users.tabStats"), icon: TrendingUp },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "users":
        return (
          <UsersTab
            users={users}
            accounts={accounts}
            loading={loading}
            error={error}
            filters={filters}
            onRefresh={loadData}
            onFilterChange={handleFilterChange}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onToggleUserStatus={handleToggleUserStatus}
          />
        );
      case "teams":
        return (
          <TeamsTab
            teams={teams}
            users={users}
            loading={loading}
            error={error}
            filters={filters}
            onRefresh={loadData}
            onFilterChange={handleFilterChange}
            onEditTeam={handleEditTeam}
            onDeleteTeam={handleDeleteTeam}
          />
        );
      case "hierarchy":
        return (
          <HierarchyTab
            users={users}
            teams={teams}
            loading={loading}
            error={error}
            onRefresh={loadData}
          />
        );
      case "stats":
        return (
          <StatsTab
            users={users}
            teams={teams}
            stats={stats}
            loading={loading}
            error={error}
            onRefresh={loadData}
          />
        );
      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t("users.selectTab")}</div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Users className="w-8 h-8 mr-3 text-[#264a3e]" />
                {t("users.pageTitle")}
              </h1>
              <p className="mt-2 text-gray-600">
                {t("users.pageDescription")}
              </p>
            </div>

            {/* Custom Buttons */}
            <div className="flex items-center space-x-3 ml-6">
              <button
                onClick={loadData}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("users.refresh")}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`${
                      activeTab === tab.id
                        ? "border-[#264a3e] text-[#264a3e]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <TabIcon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default UsersMain;
